import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, X, Search, Building2, ImagePlus } from "lucide-react";
import { API, PROP_TYPES, PROP_STATUS, emptyProp, formatError } from "./constants";

export const PropertiesView = ({ user }) => {
  const isCofounder = user.role === "cofounder";
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [propSearch, setPropSearch] = useState("");
  const [propType, setPropType] = useState("all");
  const [propStatus, setPropStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [propForm, setPropForm] = useState(emptyProp);
  const [adding, setAdding] = useState(false);
  const [uploadingId, setUploadingId] = useState("");
  const [notice, setNotice] = useState("");

  const fetchProperties = () => {
    axios
      .get(`${API}/properties`, { withCredentials: true })
      .then((res) => setProperties(res.data))
      .catch(() => setProperties([]));
  };

  useEffect(() => {
    fetchProperties();
    axios
      .get(`${API}/leads/list`, { withCredentials: true })
      .then((res) => setLeads(res.data))
      .catch(() => setLeads([]));
  }, []);

  const updateProperty = async (id, patch) => {
    setProperties((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    try {
      const res = await axios.patch(`${API}/properties/${id}`, patch, { withCredentials: true });
      if (res.data?.pending) {
        setNotice("Change request sent — another co-founder must approve it before it applies.");
        fetchProperties();
      } else if (patch.client_id !== undefined) {
        fetchProperties();
      }
    } catch (err) {
      setNotice(formatError(err.response?.data?.detail));
      fetchProperties();
    }
  };

  const addProperty = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const payload = { ...propForm, size_sqft: propForm.size_sqft ? Number(propForm.size_sqft) : null };
      await axios.post(`${API}/properties`, payload, { withCredentials: true });
      setShowAdd(false);
      setPropForm(emptyProp);
      fetchProperties();
    } catch (err) {
      setNotice(formatError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm("Remove this property permanently?")) return;
    try {
      await axios.delete(`${API}/properties/${id}`, { withCredentials: true });
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch {
    }
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/properties/${id}/photo`, fd, { withCredentials: true });
      fetchProperties();
    } catch (err) {
      setNotice(formatError(err.response?.data?.detail));
    } finally {
      setUploadingId("");
    }
  };

  const pq = propSearch.trim().toLowerCase();
  const filteredProps = properties.filter((p) => {
    if (propType !== "all" && p.type !== propType) return false;
    if (propStatus !== "all" && p.status !== propStatus) return false;
    if (pq && ![p.name, p.city, p.location].some((v) => (v || "").toLowerCase().includes(pq))) return false;
    return true;
  });

  return (
    <div data-testid="properties-view">
      {notice && (
        <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-amber/60 bg-amber/10 px-5 py-4" data-testid="property-notice">
          <p className="text-sm font-semibold text-navy">{notice}</p>
          <button onClick={() => setNotice("")} data-testid="property-notice-dismiss" className="text-gray-500 hover:text-navy" aria-label="Dismiss notice">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="property-filters">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={propSearch}
            onChange={(e) => setPropSearch(e.target.value)}
            placeholder="Search name, city, location..."
            data-testid="property-search-input"
            className="w-64 rounded-md border border-border bg-white py-2 pl-9 pr-3 text-sm text-navy outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-amber"
          />
        </div>
        <select
          value={propType}
          onChange={(e) => setPropType(e.target.value)}
          data-testid="property-type-filter"
          className="rounded-md border border-border bg-white px-3 py-2 text-xs font-bold text-gray-700 outline-none transition-colors duration-200 focus:border-amber"
        >
          <option value="all">All Types</option>
          {Object.entries(PROP_TYPES).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={propStatus}
          onChange={(e) => setPropStatus(e.target.value)}
          data-testid="property-status-filter"
          className="rounded-md border border-border bg-white px-3 py-2 text-xs font-bold text-gray-700 outline-none transition-colors duration-200 focus:border-amber"
        >
          <option value="all">All Statuses</option>
          {Object.entries(PROP_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <span className="text-xs font-semibold text-gray-500" data-testid="properties-count">
          {filteredProps.length} of {properties.length} shown
        </span>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-property-btn"
          className="ml-auto flex items-center gap-2 rounded-md bg-amber px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white"
        >
          <Plus size={14} />
          Add Property
        </button>
      </div>

      {filteredProps.length === 0 ? (
        <div className="mt-6 rounded-xl border border-border bg-white p-10 text-center shadow-soft" data-testid="properties-empty">
          <Building2 size={28} className="mx-auto text-gray-300" />
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-gray-500">
            {properties.length === 0 ? "No properties yet. Add your first plot, building or project." : "No properties match these filters."}
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-grid">
          {filteredProps.map((p, i) => (
            <div key={p.id} className="flex flex-col overflow-hidden rounded-xl border border-border bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift" data-testid="property-card">
              {p.photo_path ? (
                <img src={`${API}/files/${p.photo_path}`} alt={p.name} className="h-44 w-full object-cover" data-testid={`property-photo-${i}`} />
              ) : (
                <label
                  className="flex h-44 w-full cursor-pointer flex-col items-center justify-center gap-2 bg-cream text-gray-400 transition-colors duration-300 hover:bg-sand hover:text-amberdark"
                  data-testid={`property-photo-upload-${i}`}
                >
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(p.id, e.target.files[0])} />
                  <ImagePlus size={24} />
                  <span className="text-xs font-bold uppercase tracking-[0.15em]">
                    {uploadingId === p.id ? "Uploading..." : "Add photo"}
                  </span>
                </label>
              )}
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="rounded-full bg-cream px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-600">
                      {PROP_TYPES[p.type] || p.type}
                    </span>
                    <h3 className="mt-3 truncate font-display text-lg font-bold tracking-tight text-navy" data-testid={`property-name-${i}`}>{p.name}</h3>
                    <p className="mt-0.5 text-xs font-semibold text-gray-500">
                      {[p.location, p.city].filter(Boolean).join(", ") || "Location not set"}
                    </p>
                  </div>
                  <select
                    value={p.status}
                    onChange={(e) => updateProperty(p.id, { status: e.target.value })}
                    data-testid={`property-status-select-${i}`}
                    className={`shrink-0 cursor-pointer rounded-md border px-2 py-1.5 text-xs font-bold outline-none ${PROP_STATUS[p.status]?.cls || ""}`}
                  >
                    {Object.entries(PROP_STATUS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4 text-sm">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Size</p>
                    <p className="mt-0.5 font-bold text-navy">{p.size_sqft ? `${Number(p.size_sqft).toLocaleString("en-IN")} sq.ft` : "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Price</p>
                    <p className="mt-0.5 font-bold text-navy">{p.price || "—"}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Client</p>
                  <select
                    value={p.client_id || ""}
                    onChange={(e) => updateProperty(p.id, { client_id: e.target.value })}
                    data-testid={`property-client-select-${i}`}
                    className="mt-1 w-full cursor-pointer rounded-md border border-border bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 outline-none transition-colors duration-200 focus:border-amber"
                  >
                    <option value="">No client linked</option>
                    {leads.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}{l.city ? ` — ${l.city}` : ""}</option>
                    ))}
                  </select>
                </div>
                {p.notes && <p className="mt-3 flex-1 text-xs leading-relaxed text-gray-500">{p.notes}</p>}
                <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                  <p className="text-[10px] text-gray-400">
                    {p.added_by_name ? `Added by ${p.added_by_name}` : ""}
                  </p>
                  <div className="flex items-center gap-2">
                    {p.photo_path && (
                      <label className="cursor-pointer rounded-md border border-border px-3 py-1.5 text-xs font-bold text-gray-600 transition-colors duration-200 hover:border-amber hover:text-amberdark" data-testid={`property-photo-change-${i}`}>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(p.id, e.target.files[0])} />
                        {uploadingId === p.id ? "Uploading..." : "Change photo"}
                      </label>
                    )}
                    {isCofounder && (
                      <button
                        onClick={() => deleteProperty(p.id)}
                        data-testid={`property-delete-btn-${i}`}
                        aria-label={`Delete property ${p.name}`}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-400 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm"
          data-testid="add-property-modal"
          onClick={() => setShowAdd(false)}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-8 shadow-lift"
            data-lenis-prevent="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAdd(false)}
              data-testid="add-property-cancel"
              aria-label="Close add property form"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-border text-gray-500 transition-colors duration-300 hover:border-navy hover:text-navy"
            >
              <X size={16} />
            </button>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy" data-testid="add-property-heading">
              Add a property
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Land, buildings or projects you hold or are selling. Anyone on the
              team can add — photos can be attached from the card after saving.
            </p>
            <form onSubmit={addProperty} className="mt-7 space-y-6" data-testid="add-property-form">
              <div>
                <label htmlFor="prop-name" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Property name</label>
                <input id="prop-name" data-testid="prop-name-input" required value={propForm.name} onChange={(e) => setPropForm({ ...propForm, name: e.target.value })} placeholder="e.g. Whitefield 30x40 plot" className="field-input mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="prop-type" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Type</label>
                  <select id="prop-type" data-testid="prop-type-select" value={propForm.type} onChange={(e) => setPropForm({ ...propForm, type: e.target.value })} className="field-input mt-2">
                    {Object.entries(PROP_TYPES).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="prop-city" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">City</label>
                  <input id="prop-city" data-testid="prop-city-input" required value={propForm.city} onChange={(e) => setPropForm({ ...propForm, city: e.target.value })} placeholder="Bangalore" className="field-input mt-2" />
                </div>
              </div>
              <div>
                <label htmlFor="prop-location" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Area / locality</label>
                <input id="prop-location" data-testid="prop-location-input" value={propForm.location} onChange={(e) => setPropForm({ ...propForm, location: e.target.value })} placeholder="e.g. Whitefield, Near ITPL" className="field-input mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="prop-size" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Size (sq.ft)</label>
                  <input id="prop-size" data-testid="prop-size-input" type="number" min="0" value={propForm.size_sqft} onChange={(e) => setPropForm({ ...propForm, size_sqft: e.target.value })} placeholder="1200" className="field-input mt-2" />
                </div>
                <div>
                  <label htmlFor="prop-price" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Price</label>
                  <input id="prop-price" data-testid="prop-price-input" value={propForm.price} onChange={(e) => setPropForm({ ...propForm, price: e.target.value })} placeholder="₹85 Lakhs" className="field-input mt-2" />
                </div>
              </div>
              <div>
                <label htmlFor="prop-client" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Link a client (optional)</label>
                <select id="prop-client" data-testid="prop-client-select" value={propForm.client_id} onChange={(e) => setPropForm({ ...propForm, client_id: e.target.value })} className="field-input mt-2">
                  <option value="">No client linked</option>
                  {leads.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}{l.city ? ` — ${l.city}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="prop-notes" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Notes (optional)</label>
                <textarea id="prop-notes" data-testid="prop-notes-input" rows={2} value={propForm.notes} onChange={(e) => setPropForm({ ...propForm, notes: e.target.value })} placeholder="Facing, floor, documents status..." className="field-input mt-2 resize-none" />
              </div>
              <button
                type="submit"
                data-testid="add-property-submit"
                disabled={adding}
                className="w-full rounded-md bg-amber px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50"
              >
                {adding ? "Adding..." : "Add Property"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
